import { useEffect, useState } from "react";
import { Gauge, X } from "lucide-react";

function Row({ label, value, testId, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <span
        data-testid={testId}
        className={`mono text-[11px] ${tone || "text-slate-800"}`}
      >
        {value}
      </span>
    </div>
  );
}

/** Live render telemetry HUD — draw calls, culling, memory, load timings. */
export function ViewerStatsPanel({ viewer, loadTelemetry, onClose }) {
  const [t, setT] = useState(null);

  useEffect(() => {
    const tick = () => setT(viewer.current?.telemetry?.() || null);
    tick();
    const id = setInterval(tick, 600);
    return () => clearInterval(id);
  }, [viewer]);

  const fpsTone = !t ? "" : t.fps >= 45 ? "text-emerald-600" : t.fps >= 24 ? "text-amber-600" : "text-red-600";

  return (
    <div
      data-testid="viewer-stats-panel"
      className="pointer-events-auto absolute bottom-14 right-4 z-20 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-300 bg-white/95 p-3 shadow-2xl backdrop-blur-md"
    >
      <div className="mb-2 flex items-center gap-2 border-b border-slate-200 pb-2">
        <Gauge size={14} className="text-cyan-600" />
        <span className="mono text-[10px] uppercase tracking-[0.18em] text-cyan-700">
          Performance
        </span>
        <button
          data-testid="viewer-stats-close"
          onClick={onClose}
          className="ml-auto grid h-5 w-5 place-items-center rounded-full text-slate-500 hover:bg-slate-100"
        >
          <X size={12} />
        </button>
      </div>

      {t ? (
        <div className="space-y-1">
          <Row label="FPS" value={t.fps} testId="stat-fps" tone={fpsTone} />
          <Row label="Frame" value={`${t.frameMs} ms`} testId="stat-frame-ms" />
          <Row label="Draw calls" value={t.drawCalls} testId="stat-draw-calls" />
          <Row
            label="Triangles / frame"
            value={t.trianglesRendered.toLocaleString()}
            testId="stat-triangles"
          />
          <Row
            label="Meshes (culled)"
            value={`${t.visibleMeshes} (${t.culledMeshes})`}
            testId="stat-meshes"
          />
          <Row label="Instances" value={t.instances.toLocaleString()} testId="stat-instances" />
          <Row
            label="Panels"
            value={`${t.panels.toLocaleString()}${t.hiddenPanels ? ` · ${t.hiddenPanels} hidden` : ""}`}
            testId="stat-panels"
          />
          <Row label="Geometry memory" value={`${t.gpuMB} MB`} testId="stat-gpu-mb" />
          <Row
            label="JS heap"
            value={t.heapMB != null ? `${t.heapMB} MB` : "n/a"}
            testId="stat-heap-mb"
          />
          <Row
            label="Ghost mode"
            value={t.ghosting ? "on" : "off"}
            testId="stat-ghosting"
            tone={t.ghosting ? "text-cyan-700" : "text-slate-500"}
          />
        </div>
      ) : (
        <p className="text-[11px] text-slate-500">Collecting telemetry…</p>
      )}

      {loadTelemetry ? (
        <div className="mt-2 space-y-1 border-t border-slate-200 pt-2">
          <Row
            label="Source"
            value={loadTelemetry.cached ? "browser cache" : "server download"}
            testId="stat-load-source"
            tone={loadTelemetry.cached ? "text-emerald-600" : "text-slate-800"}
          />
          <Row
            label="Download"
            value={`${((loadTelemetry.bytes || 0) / 1048576).toFixed(1)} MB · ${loadTelemetry.downloadMs} ms`}
            testId="stat-download"
          />
          <Row label="GPU build" value={`${loadTelemetry.buildMs} ms`} testId="stat-build-ms" />
          <Row label="Total load" value={`${loadTelemetry.totalMs} ms`} testId="stat-total-ms" />
        </div>
      ) : null}
    </div>
  );
}
